import {
  Breadcrumbs,
  CenteredContainer,
  Column,
  Container,
  DocumentCreateForm,
  EmptyScreen,
  Row,
  ScrollableWrapper
} from '@cdab/scania/qpr/components'
import { getClient } from '@cdab/scania/qpr/contexts/backend-provider'
import { useTitle } from '@cdab/scania/qpr/contexts/title'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import type { User } from '@cdab/scania/qpr/schema'
import {
  Roles,
  type AuditPointNumber,
  type CheckPointNumber,
  type Market
} from '@cdab/scania/qpr/schema'
import { capitalizeAllFirstLetters } from '@cdab/utils'
import { TdsIcon } from '@scania/tegel-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  isRouteErrorResponse,
  json,
  useLoaderData,
  useRouteError
} from 'react-router'

//Temp solution, since we are using only one version now
//Later we can use it if we will add supporting of diff DOS versions
const CurrentDOSVersionId = 6 // DOS 5

export type NewAdminDocumentLoaderData = {
  auditPoints: AuditPointNumber[]
  checkPoints: CheckPointNumber[]
  allMarkets: Market[]
  currentUser: User
}

export async function loader(): Promise<NewAdminDocumentLoaderData> {
  try {
    const client = getClient()
    const userInfo = await client.GetuserInfo()
    if (!userInfo) {
      throw json({
        status: 401
      })
    }
    const currentUser = await client.UserService.GetUser(userInfo.userId)

    if (currentUser.role < Roles.CoOrdinator) {
      throw json({
        status: 403
      })
    }

    let allMarkets: Market[] = []
    if (currentUser.role === Roles.CoOrdinator) {
      // If role is CoOrdinator, get markets specific to the user
      allMarkets = await client.MarketsService.GetMarketsForUser(
        userInfo.userId
      )
    }

    if (currentUser.role > Roles.CoOrdinator) {
      // If role is greater than CoOrdinator, get all markets
      allMarkets = await client.MarketsService.GetAllMarkets()
    }

    const [auditPoints, checkPoints] = await Promise.all([
      client.AuditsService.GetAuditPointNumbers(CurrentDOSVersionId),
      client.AuditsService.GetCheckPointNumbers(CurrentDOSVersionId)
    ])

    return {
      auditPoints,
      checkPoints,
      allMarkets,
      currentUser
    }
  } catch (error) {
    if (error instanceof Response) {
      throw new Response(null, {
        status: error.status
      })
    } else {
      throw new Response(null, {
        status: 404
      })
    }
  }
}

export function Page() {
  const { t } = useTranslation(['documents', 'common'])
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const { auditPoints, checkPoints, allMarkets, currentUser } =
    useLoaderData() as NewAdminDocumentLoaderData
  const { updateTitles } = useTitle()

  useEffect(() => {
    updateTitles({
      contentHeader: {
        subtitle: capitalizeAllFirstLetters(
          t('reference-documents', { ns: 'common' })
        ),
        title: t('administration', { ns: 'common' })
      },
      mobile: {
        title: `${capitalizeAllFirstLetters(
          t('reference-documents', { ns: 'common' })
        )}`,
        description: null
      }
    })
  }, [t])

  return (
    <ScrollableWrapper>
      <Container
        fullHeight
        fluid='normal'
        paddingOnColumns={isLg}
        paddingOnContainer={isLg}
      >
        <Row>
          <Column width={12} className='tds-u-pt2'>
            <Breadcrumbs
              links={[
                {
                  text: capitalizeAllFirstLetters(
                    t('reference-documents', { ns: 'common' })
                  ),
                  to: `/admin/documents/all`
                },
                { text: t('new', { ns: 'common' }), to: '' }
              ]}
            />
          </Column>
        </Row>
        <Row>
          <DocumentCreateForm
            auditPoints={auditPoints}
            checkPoints={checkPoints}
            allMarkets={allMarkets}
            currentUser={currentUser}
          />
        </Row>
      </Container>
    </ScrollableWrapper>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation('errors')
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    let message = t('talk-to-admin')

    if (error.status === 403) {
      message = t('access-denied')
    }

    return (
      <CenteredContainer
        fluid='normal'
        paddingOnColumns={!isLg}
        paddingOnContainer={!isLg}
      >
        <Row>
          <Column fullHeight width={12} padding={false}>
            <EmptyScreen
              title={t('could-not-load-page')}
              description={message}
              icon={<TdsIcon name='warning' size='lg' />}
            />
          </Column>
        </Row>
      </CenteredContainer>
    )
  }

  // rethrow to let the parent error boundary handle it
  // when it's not a special case for this route
  throw error
}
